/**
 * 050 T013 / T014 / T015 — the report composer.
 *
 * Left, the choices. Right, the actual report, rendered by the same components the emailed PDF
 * uses. There is no separate "preview" implementation, because a preview that is not the artefact
 * is a promise the product may not keep.
 *
 * Sections render in the order they are listed, and the order is editable here, so moving a section
 * moves it in the email too.
 */
import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ArrowDown, ArrowUp, Link2, Save, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { AdminOnly } from '@/components/admin-only'
import { btnPrimary, btnSecondary, btnGhost, Card, SkeletonRows } from '@/components/rm'
import { Field, Input, Select } from '@/components/rm/form'
import { useCompany, useTeam } from '@/hooks/use-company'
import { ReportHeader, ReportSections } from '@/components/report/sections'
import { phFormat } from '@/lib/ph'
import {
  ALL_SECTIONS, BLANK_CONFIG, DAYS, rangeLabel, resolveRange, useDeleteReportSchedule,
  useReportMutations, useReportSchedule, useReportSections, useReportShares, useReportTemplate,
  useSaveReportSchedule, type RangeKind, type ReportConfig, type SectionId,
} from '@/hooks/use-reports'

export const Route = createFileRoute('/_app/reports_/$id')({
  component: () => <AdminOnly><Composer /></AdminOnly>,
})

const FALLBACK_ACCENT = '#0038a8'

function Composer() {
  const { id } = Route.useParams()
  const nav = useNavigate()
  const isNew = id === 'new'

  const { data: template, isLoading } = useReportTemplate(id)
  const { company } = useCompany()
  const { members } = useTeam()
  const { save, share } = useReportMutations()
  const shares = useReportShares(id)
  const schedule = useReportSchedule(id)
  const saveSchedule = useSaveReportSchedule()
  const deleteSchedule = useDeleteReportSchedule()

  const [name, setName] = useState('Weekly report')
  const [config, setConfig] = useState<ReportConfig>(BLANK_CONFIG)
  const [loaded, setLoaded] = useState(false)

  // Load once. Re-seeding on every background refetch would discard edits made while one was in
  // flight — the same trap the workflow canvas has.
  useEffect(() => {
    if (loaded || isNew || !template) return
    setName(template.name)
    setConfig(template.config)
    setLoaded(true)
  }, [template, isNew, loaded])

  const range = useMemo(() => resolveRange(config), [config])
  const accent = config.accent || company?.accent_color || FALLBACK_ACCENT

  const data = useReportSections(range.from, range.to, config.sections, config.user_ids)

  const patch = (p: Partial<ReportConfig>) => setConfig((c) => ({ ...c, ...p }))

  const toggleSection = (sid: SectionId) =>
    patch({
      sections: config.sections.includes(sid)
        ? config.sections.filter((s) => s !== sid)
        : [...config.sections, sid],
    })

  const move = (sid: SectionId, delta: -1 | 1) => {
    const i = config.sections.indexOf(sid)
    const j = i + delta
    if (i < 0 || j < 0 || j >= config.sections.length) return
    const next = [...config.sections]
    ;[next[i], next[j]] = [next[j], next[i]]
    patch({ sections: next })
  }

  const doSave = async () => {
    try {
      const newId = await save.mutateAsync({ id: isNew ? undefined : id, name, config })
      toast.success('Report saved')
      if (isNew) nav({ to: '/reports/$id', params: { id: newId } })
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not save') }
  }

  const doShare = async () => {
    if (isNew) return toast.error('Save the report first')
    try {
      const token = await share.mutateAsync({ templateId: id, from: range.from, to: range.to })
      const url = `${window.location.origin}/r/${token}`
      await navigator.clipboard.writeText(url).catch(() => {})
      shares.refetch()
      toast.success('Link copied', {
        description: `It shows ${range.from} to ${range.to} and will keep showing that period.`,
      })
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not create a link') }
  }

  if (isLoading && !isNew) return <div className="p-6"><SkeletonRows count={4} /></div>

  return (
    <div className="min-h-full bg-canvas">
      <div className="flex flex-wrap items-center gap-2 border-b border-token bg-token-surface px-3 py-2.5 sm:px-4 print:hidden">
        <button type="button" className={btnGhost} onClick={() => nav({ to: '/reports' })} aria-label="Back to reports">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <input
          className="min-w-0 flex-1 rounded-[10px] border border-transparent bg-transparent px-2 py-1 text-[15px] font-semibold text-token-primary outline-none hover:border-token focus:border-brand"
          value={name} aria-label="Report name" onChange={(e) => setName(e.target.value)}
        />
        <button type="button" className={btnSecondary} onClick={doShare} disabled={isNew || share.isPending}>
          <Link2 className="h-4 w-4" /> Share link
        </button>
        <button type="button" className={btnPrimary} onClick={doSave} disabled={save.isPending}>
          <Save className="h-4 w-4" /> Save
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Choices */}
        <aside className="w-full shrink-0 space-y-4 border-b border-token bg-token-surface p-4 lg:w-[320px] lg:border-b-0 lg:border-r print:hidden">
          <Field label="Period">
            <Select value={config.range_kind} onChange={(e) => patch({ range_kind: e.target.value as RangeKind })}>
              {(['trailing_7d', 'trailing_30d', 'last_month', 'custom'] as RangeKind[]).map((k) => (
                <option key={k} value={k}>{rangeLabel(k)}</option>
              ))}
            </Select>
          </Field>

          {config.range_kind === 'custom' && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="From">
                <Input type="date" value={config.range_start ?? range.from} onChange={(e) => patch({ range_start: e.target.value })} />
              </Field>
              <Field label="To">
                <Input type="date" value={config.range_end ?? range.to} onChange={(e) => patch({ range_end: e.target.value })} />
              </Field>
            </div>
          )}

          <div>
            <p className="text-[13px] font-semibold text-token-secondary">Sections</p>
            <p className="mt-0.5 text-[11.5px] text-token-muted">They print in this order.</p>
            <ul className="mt-2 space-y-1">
              {[...config.sections, ...ALL_SECTIONS.map((s) => s.id).filter((s) => !config.sections.includes(s))]
                .map((sid) => {
                  const meta = ALL_SECTIONS.find((s) => s.id === sid)!
                  const on = config.sections.includes(sid)
                  return (
                    <li key={sid} className="flex items-start gap-2 rounded-[10px] border border-token p-2">
                      <input
                        type="checkbox" className="mt-1 h-4 w-4 accent-brand"
                        checked={on} onChange={() => toggleSection(sid)}
                        aria-label={meta.label}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium text-token-primary">{meta.label}</p>
                        <p className="text-[11.5px] leading-snug text-token-secondary">{meta.hint}</p>
                      </div>
                      {on && (
                        <div className="flex flex-col">
                          <button type="button" className={btnGhost} aria-label={`Move ${meta.label} up`} onClick={() => move(sid, -1)}>
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button type="button" className={btnGhost} aria-label={`Move ${meta.label} down`} onClick={() => move(sid, 1)}>
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </li>
                  )
                })}
            </ul>
          </div>

          <Field label="Only these people">
            <Select
              multiple
              className="h-auto min-h-[92px]"
              value={config.user_ids}
              onChange={(e) => patch({ user_ids: [...e.target.selectedOptions].map((o) => o.value) })}
            >
              {members.map((m) => (
                <option key={m.id} value={m.id}>{[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email}</option>
              ))}
            </Select>
            <p className="mt-1 text-[11.5px] text-token-muted">Select nobody for the whole team.</p>
          </Field>

          <label className="flex items-center gap-2 text-[13.5px] text-token-primary">
            <input type="checkbox" className="h-4 w-4 accent-brand" checked={config.show_logo} onChange={(e) => patch({ show_logo: e.target.checked })} />
            Show the company logo
          </label>

          <Field label="Accent colour">
            <div className="flex items-center gap-2">
              <input
                type="color" className="h-9 w-12 cursor-pointer rounded-[8px] border border-token bg-token-surface"
                value={accent} onChange={(e) => patch({ accent: e.target.value })} aria-label="Accent colour"
              />
              <button type="button" className={btnGhost} onClick={() => patch({ accent: null })}>
                Use the company colour
              </button>
            </div>
          </Field>

          {/* T017 — the Monday morning email. Hidden until the report is saved, because a schedule
              needs something to point at. */}
          {!isNew && (
            <div className="rounded-[12px] border border-token p-3">
              <p className="text-[13px] font-semibold text-token-secondary">Email it weekly</p>
              {schedule.data ? (
                <>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Field label="Day">
                      <Select
                        value={String(schedule.data.day_of_week)}
                        onChange={(e) => saveSchedule.mutate({ ...schedule.data!, report_template_id: id, day_of_week: Number(e.target.value) })}
                      >
                        {DAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                      </Select>
                    </Field>
                    <Field label="Hour">
                      <Select
                        value={String(schedule.data.send_hour)}
                        onChange={(e) => saveSchedule.mutate({ ...schedule.data!, report_template_id: id, send_hour: Number(e.target.value) })}
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                  <p className="mt-1 text-[11.5px] text-token-muted">
                    Your business's local time, not the reader's.
                  </p>

                  <Field label="Also send to these addresses" className="mt-2">
                    <Input
                      placeholder="accountant@example.com, owner@example.com"
                      defaultValue={schedule.data.recipients?.emails?.join(', ') ?? ''}
                      onBlur={(e) => saveSchedule.mutate({
                        ...schedule.data!, report_template_id: id,
                        recipients: {
                          user_ids: schedule.data!.recipients?.user_ids ?? [],
                          emails: e.target.value.split(',').map((x) => x.trim()).filter(Boolean),
                        },
                      })}
                    />
                  </Field>

                  <label className="mt-2 flex items-center gap-2 text-[13px] text-token-primary">
                    <input
                      type="checkbox" className="h-4 w-4 accent-brand"
                      checked={schedule.data.enabled}
                      onChange={(e) => saveSchedule.mutate({ ...schedule.data!, report_template_id: id, enabled: e.target.checked })}
                    />
                    Send it
                  </label>

                  {/* last_sent_at is shown whether or not it has ever fired. A schedule that
                      silently stopped looks identical to one that is working until you can see
                      when it last went out — which is how 028's booking confirmations were dead
                      for days with nothing recording it. */}
                  <p className="mt-2 text-[11.5px] text-token-secondary">
                    {schedule.data.last_sent_at
                      ? `Last sent ${phFormat(new Date(schedule.data.last_sent_at), 'datetime', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`
                      : 'Never sent yet'}
                    {schedule.data.enabled && schedule.data.next_due_at
                      ? ` · next ${phFormat(new Date(schedule.data.next_due_at), 'datetime', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`
                      : schedule.data.enabled ? '' : ' · paused'}
                  </p>
                  {schedule.data.attempts > 0 && (
                    <p className="mt-1 text-[11.5px] font-medium text-token-danger">
                      {schedule.data.attempts} failed attempt{schedule.data.attempts === 1 ? '' : 's'} since the last success.
                    </p>
                  )}

                  <button
                    type="button" className={`${btnGhost} mt-1`}
                    onClick={() => deleteSchedule.mutate(schedule.data!.id, { onSuccess: () => toast.success('Weekly email removed') })}
                  >
                    Remove the weekly email
                  </button>
                </>
              ) : (
                <>
                  <p className="mt-0.5 text-[11.5px] leading-snug text-token-secondary">
                    Have it arrive every Monday morning with a live link, covering the week that just finished.
                  </p>
                  <button
                    type="button" className={`${btnSecondary} mt-2`}
                    disabled={saveSchedule.isPending}
                    onClick={() => saveSchedule.mutate(
                      { report_template_id: id, day_of_week: 1, send_hour: 7, enabled: true, recipients: { user_ids: [], emails: [] } },
                      { onSuccess: () => { schedule.refetch(); toast.success('Set for Monday at 7am') } },
                    )}
                  >
                    Send it weekly
                  </button>
                </>
              )}
            </div>
          )}

          {shares.data && shares.data.length > 0 && (
            <div>
              <p className="text-[13px] font-semibold text-token-secondary">Links you have shared</p>
              <ul className="mt-1.5 space-y-1">
                {shares.data.slice(0, 5).map((s) => (
                  <li key={s.token} className="text-[11.5px] text-token-secondary">
                    {s.range_start} to {s.range_end}
                    {s.expires_at && ` · expires ${phFormat(new Date(s.expires_at), 'date', { day: 'numeric', month: 'short' })}`}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-[11px] text-token-muted">Each one keeps showing its own period, not today's.</p>
            </div>
          )}
        </aside>

        {/* The report itself, rendered by the components the PDF uses. */}
        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <Card className="mx-auto max-w-3xl p-6 print:border-0 print:p-0 print:shadow-none">
            <ReportHeader
              title={name}
              from={range.from}
              to={range.to}
              timezone={data.data?.range?.timezone}
              branding={{ accent, companyName: company?.name, logo: company?.logo, showLogo: config.show_logo }}
            />
            {data.isLoading ? (
              <div className="flex items-center gap-2 py-10 text-[13px] text-token-secondary">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Working out the numbers…
              </div>
            ) : config.sections.length === 0 ? (
              <p className="py-10 text-center text-[13px] text-token-secondary">Choose at least one section.</p>
            ) : (
              <ReportSections sections={config.sections} data={data.data ?? {}} accent={accent} />
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
